import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { PLANS, formatPrice, annualSavings, ANNUAL_DISCOUNT_PCT } from '../config/plans';
import { api } from '../utils/api';
import { analytics, EVENTS } from '../utils/analytics';
import { DemoRequestModal } from '../components/DemoRequestModal';
import { SEO } from '../components/SEO';

/**
 * Public pricing page for Kids WiseApp.
 *
 * URL: /pricing
 *
 * Behaviour:
 *   - Anyone can view (no auth needed)
 *   - "Start free trial" CTAs:
 *       * Not logged in → route to /register so they create an org first
 *       * Logged in admin → kick off Mollie Checkout for the plan
 *       * Logged in non-admin → toast "ask your admin to upgrade"
 *   - "Contact sales" CTA → mailto with prefilled subject
 *   - Monthly/Annual toggle re-prices every card (annual = 17% off)
 */
function Pricing() {
  const navigate = useNavigate();
  const [interval, setInterval] = useState('monthly');
  const [busyPlan, setBusyPlan] = useState(null);
  // Controls the "Book a 15-min demo" modal invoked from the compare
  // hero and the "not sure which plan?" nudge underneath the cards.
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoSource, setDemoSource] = useState('pricing');
  const openDemo = (source) => { setDemoSource(source); setDemoOpen(true); };

  // Fire once per mount so PostHog can build a landing→pricing funnel.
  useEffect(() => {
    analytics.track(EVENTS.PRICING_VIEWED, {
      referrer: (typeof document !== 'undefined' && document.referrer) || null,
    });
  }, []);

  // Pull the user lazily from localStorage so the page works pre-auth too.
  const sessionUser = (() => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const isAdmin = sessionUser && ['admin', 'org_owner', 'org_admin'].includes(sessionUser.role);

  async function handleCta(plan) {
    if (plan.isCustom) {
      window.location.href = 'mailto:sales@kidswiseapp.com'
        + '?subject=' + encodeURIComponent('Enterprise enquiry')
        + '&body=' + encodeURIComponent(
          'Hi Kids WiseApp team,\n\nWe\'d like to learn more about the Enterprise plan.\n\n'
          + 'Number of settings: \nApprox total children: \nCurrent system: \n\nThanks.',
        );
      return;
    }
    // Not signed in → route to the guided nursery-owner signup wizard,
    // preserving the chosen plan + interval so step 1 pre-selects it.
    if (!sessionUser) {
      navigate(`/register-your-nursery?plan=${plan.key}&interval=${interval}`);
      return;
    }
    // Signed in non-admin → can't self-checkout.
    if (!isAdmin) {
      toast.error("Only the organisation admin can change the subscription. Please ask them to upgrade.");
      return;
    }
    // Logged-in admin → start Mollie Checkout (first payment + mandate).
    setBusyPlan(plan.key);
    try {
      const { url, session_id } = await api.startBillingCheckout({ planKey: plan.key, interval });
      // Belt-and-braces: stash the session_id locally in case Mollie's
      // redirect strips or mangles the query string. `BillingSuccess.js`
      // reads this as a fallback so we can still confirm the payment.
      if (session_id) {
        try { localStorage.setItem('kw_last_billing_session', session_id); } catch { /* ignore */ }
      }
      window.location.href = url;
    } catch (err) {
      const msg = err.message || 'Could not start checkout';
      toast.error(msg);
      // If the env isn't wired for Mollie yet, surface a friendly hint.
      if (msg.toLowerCase().includes('not yet configured') || msg.toLowerCase().includes('not configured')) {
        toast.message('Tip: add a real MOLLIE_API_KEY in backend/.env to enable checkout.', { duration: 6000 });
      }
    } finally {
      setBusyPlan(null);
    }
  }

  return (
    <main data-testid="pricing-page" style={pageStyle}>
      <SEO
        title="Pricing — Plans for every childcare setting"
        description="Transparent monthly and annual pricing for childcare providers — from single childminders to multi-site nursery groups. Parent app, daily register, and chain-of-custody included in every plan."
        canonical="https://kidswiseapp.com/pricing"
        image="https://kidswiseapp.com/og-image.png"
      />
      {/* Header */}
      <header style={headerStyle}>
        <div style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto' }}>
          <div style={brandLine}>Kids WiseApp</div>
          <h1 style={h1Style}>Simple pricing for every setting</h1>
          <p style={subStyle}>
            Whether you run a single childminding business or a multi-site nursery group,
            there's a plan that fits. All plans include parent app, daily register, and our
            full safeguarding toolkit.
          </p>
          {/* Book-a-demo affordance in the hero — the primary "Start free trial"
              CTA still lives on each card below. This gives owners who want a
              human before self-serving a low-commitment path. */}
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => openDemo('pricing_header')}
              data-testid="pricing-header-book-demo-button"
              style={{
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.35)',
                color: 'white',
                padding: '0.55rem 1.2rem',
                borderRadius: 999,
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              📅 Prefer to talk first? Book a 15-min demo
            </button>
          </div>

          {/* Interval toggle */}
          <div style={intervalToggle} data-testid="pricing-interval-toggle">
            {[
              { v: 'monthly', label: 'Monthly' },
              { v: 'annual', label: `Annual · save ${ANNUAL_DISCOUNT_PCT}%` },
            ].map((opt) => {
              const on = interval === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setInterval(opt.v)}
                  data-testid={`pricing-interval-${opt.v}`}
                  style={{
                    ...intervalBtn,
                    background: on ? '#fff' : 'transparent',
                    color: on ? '#0b3d3a' : 'rgba(255,255,255,0.85)',
                    fontWeight: on ? 700 : 600,
                    boxShadow: on ? '0 8px 18px rgba(0,0,0,0.16)' : 'none',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Cards */}
      <section style={cardsWrap} data-testid="pricing-cards">
        <div style={cardsGrid}>
          {PLANS.map((plan) => {
            const priceStr = formatPrice(plan, interval);
            const savings = interval === 'annual' ? annualSavings(plan) : null;
            const intervalSuffix = interval === 'annual' ? '/yr' : '/mo';
            return (
              <div
                key={plan.key}
                data-testid={`pricing-card-${plan.key}`}
                style={{
                  ...planCard,
                  ...(plan.recommended ? planCardRecommended : {}),
                }}
              >
                {plan.recommended && (
                  <div style={recommendedRibbon}>★ Most popular</div>
                )}
                <div style={planIconStyle}>{plan.icon}</div>
                <h2 style={planNameStyle}>{plan.name}</h2>
                <p style={planTagline}>{plan.tagline}</p>

                <div style={priceBlock}>
                  <div style={priceLine}>
                    <span style={priceAmount}>{priceStr}</span>
                    {!plan.prices.fromPrice && (
                      <span style={priceSuffix}>{intervalSuffix}</span>
                    )}
                  </div>
                  {savings && (
                    <div style={savingsBadge} data-testid={`pricing-savings-${plan.key}`}>
                      {savings}
                    </div>
                  )}
                  {plan.trialDays > 0 && (
                    <div style={trialBadge}>{plan.trialDays}-day free trial · no card needed</div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => handleCta(plan)}
                  disabled={busyPlan === plan.key}
                  data-testid={`pricing-cta-${plan.key}`}
                  style={{
                    ...ctaBtn,
                    ...(plan.recommended ? ctaBtnPrimary : ctaBtnSecondary),
                    opacity: busyPlan === plan.key ? 0.7 : 1,
                  }}
                >
                  {busyPlan === plan.key ? 'Starting…' : `${plan.ctaLabel} →`}
                </button>

                <ul style={featureList}>
                  {plan.features.map((f) => (
                    <li key={f} style={featureItem}>
                      <span style={featureCheck}>✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                  {(plan.excludedFeatures || []).map((f) => (
                    <li key={f} style={featureItemExcluded}>
                      <span style={featureCross}>—</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Footer */}
      <section style={footerWrap}>
        <div style={{ maxWidth: 920, margin: '0 auto', textAlign: 'center' }}>
          <h3 style={{ color: '#0b3d3a', fontSize: '1.3rem', margin: '0 0 0.5rem 0' }}>
            Questions?
          </h3>
          <p style={{ color: '#446f6b', fontSize: '0.95rem', margin: 0 }}>
            Get in touch with our team at{' '}
            <a
              href="mailto:sales@kidswiseapp.com"
              style={{ color: '#0b3d3a', fontWeight: 600 }}
              data-testid="pricing-mailto"
            >
              sales@kidswiseapp.com
            </a>
            {' '}— we'll respond within one business day. Prefer a live walkthrough?{' '}
            <button
              type="button"
              onClick={() => openDemo('pricing_footer')}
              data-testid="pricing-footer-book-demo-button"
              style={{ background: 'none', border: 'none', color: '#0b3d3a', fontWeight: 600, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
            >
              Book a 15-min demo →
            </button>
          </p>
          <p style={{ marginTop: '1.5rem', fontSize: '0.78rem', color: '#7a9794' }}>
            All prices in GBP. VAT charged where applicable. Cancel anytime.
            Already have an account?{' '}
            <Link to="/login" data-testid="pricing-login-link" style={{ color: '#0b3d3a', fontWeight: 600 }}>
              Sign in →
            </Link>
          </p>
        </div>
      </section>
      <DemoRequestModal open={demoOpen} onClose={() => setDemoOpen(false)} source={demoSource} />
    </main>
  );
}

// ---------- Styles ----------

const pageStyle = {
  minHeight: '100vh',
  background: '#f6fbfa',
  color: '#0b3d3a',
  fontFamily: 'inherit',
};

const headerStyle = {
  background: 'linear-gradient(180deg, hsl(189 53% 25%) 0%, hsl(189 60% 18%) 100%)',
  color: '#fff',
  padding: '4rem 1.5rem 7rem 1.5rem',
};

const brandLine = {
  fontSize: '0.74rem',
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#9fd6d1',
  marginBottom: '0.6rem',
  fontWeight: 600,
};

const h1Style = {
  fontSize: '2.4rem',
  fontWeight: 700,
  letterSpacing: '-0.015em',
  margin: '0 0 0.7rem 0',
};

const subStyle = {
  fontSize: '1.05rem',
  color: '#cfeeeb',
  margin: '0 auto 2rem auto',
  maxWidth: 620,
  lineHeight: 1.55,
};

const intervalToggle = {
  display: 'inline-flex',
  background: 'rgba(0,0,0,0.22)',
  borderRadius: 999,
  padding: '0.3rem',
};

const intervalBtn = {
  border: 'none',
  borderRadius: 999,
  padding: '0.5rem 1.2rem',
  fontSize: '0.88rem',
  cursor: 'pointer',
  transition: 'background 150ms ease, color 150ms ease',
};

const cardsWrap = {
  marginTop: '-5rem',
  padding: '0 1.5rem 3rem 1.5rem',
};

const cardsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: '1.25rem',
  maxWidth: 1200,
  margin: '0 auto',
};

const planCard = {
  background: '#fff',
  borderRadius: 18,
  padding: '1.6rem 1.4rem 1.5rem 1.4rem',
  border: '1px solid #d8e6e3',
  boxShadow: '0 6px 22px rgba(0,0,0,0.07)',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
};

const planCardRecommended = {
  border: '2px solid hsl(189 53% 35%)',
  boxShadow: '0 14px 38px rgba(11,61,58,0.18)',
  transform: 'translateY(-6px)',
};

const recommendedRibbon = {
  position: 'absolute',
  top: -14,
  right: 18,
  background: 'hsl(189 53% 25%)',
  color: '#fff',
  fontSize: '0.7rem',
  fontWeight: 700,
  padding: '0.3rem 0.7rem',
  borderRadius: 999,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const planIconStyle = { fontSize: '1.7rem', marginBottom: '0.35rem' };
const planNameStyle = { fontSize: '1.3rem', margin: '0 0 0.35rem 0', fontWeight: 700 };
const planTagline = { color: '#5d7f7c', margin: '0 0 1.1rem 0', fontSize: '0.86rem', minHeight: '2.6em' };

const priceBlock = { marginBottom: '1.1rem' };
const priceLine = { display: 'flex', alignItems: 'baseline', gap: '0.25rem' };
const priceAmount = { fontSize: '2.1rem', fontWeight: 700, color: '#0b3d3a', letterSpacing: '-0.01em' };
const priceSuffix = { color: '#5d7f7c', fontWeight: 600, fontSize: '0.95rem' };

const savingsBadge = {
  display: 'inline-block',
  background: '#dbf5ef',
  color: '#0b6b58',
  padding: '0.15rem 0.55rem',
  borderRadius: 999,
  fontSize: '0.72rem',
  fontWeight: 700,
  marginTop: '0.35rem',
};

const trialBadge = {
  marginTop: '0.5rem',
  fontSize: '0.74rem',
  color: '#5d7f7c',
};

const ctaBtn = {
  display: 'block',
  width: '100%',
  padding: '0.7rem 1rem',
  borderRadius: 12,
  fontSize: '0.92rem',
  fontWeight: 700,
  cursor: 'pointer',
  border: '1px solid transparent',
  transition: 'transform 120ms ease, box-shadow 120ms ease',
};

const ctaBtnPrimary = {
  background: 'hsl(189 53% 25%)',
  color: '#fff',
};
const ctaBtnSecondary = {
  background: '#fff',
  color: 'hsl(189 53% 25%)',
  borderColor: 'hsl(189 53% 25%)',
};

const featureList = {
  listStyle: 'none',
  padding: 0,
  margin: '1.2rem 0 0 0',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.45rem',
};

const featureItem = {
  display: 'flex',
  gap: '0.55rem',
  fontSize: '0.86rem',
  color: '#234c49',
  lineHeight: 1.45,
};
const featureItemExcluded = { ...featureItem, color: '#9ab1ae' };

const featureCheck = {
  flexShrink: 0,
  width: 20,
  height: 20,
  borderRadius: '50%',
  background: '#dbf5ef',
  color: '#0b6b58',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.74rem',
  fontWeight: 800,
};
const featureCross = {
  ...featureCheck,
  background: '#f1f5f4',
  color: '#9ab1ae',
};

const footerWrap = {
  background: '#fff',
  padding: '3rem 1.5rem 4rem 1.5rem',
  borderTop: '1px solid #e3eeec',
};

export default Pricing;
